/**
 * A utility function that combines class names conditionally, similar to the clsx library.
 * @param {...(string|Object|Array)} args - Any number of arguments that can be class names, objects, or arrays
 * @returns {string} A string of combined class names
 * 
 * @example
 * // Strings
 * cn('foo', 'bar'); // => 'foo bar'
 * 
 * // Objects
 * cn({ foo: true, bar: false }); // => 'foo'
 * 
 * // Arrays
 * cn(['foo', { bar: true }]); // => 'foo bar'
 * 
 * // Multiple types
 * cn('foo', { bar: true, baz: false }, ['qux', { quux: true }]); // => 'foo bar qux quux'
 */
export const cn = (...args: (string | object | Array<string | object> | undefined)[]) => {
  const classes: string[] = [];

  args.forEach(arg => {
    if (!arg) return;

    const argType = typeof arg;

    if (argType === 'string' || argType === 'number') {
      classes.push(arg as string);
    } else if (Array.isArray(arg)) {
      if (arg.length) {
        const inner = cn(...arg);
        if (inner) {
          classes.push(inner);
        }
      }
    } else if (argType === 'object') {
      if (arg.toString !== Object.prototype.toString) {
        classes.push(arg.toString());
      } else {
        for (const key in arg as object) {
          if (Object.hasOwn(arg as object, key) && arg[key as keyof typeof arg]) {
            classes.push(key);
          }
        }
      }
    }
  });

  return classes.join(' ') as string;
};

export default cn;
